import _ from 'lodash';
import moment from 'moment';

import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from './constants';

/**
 * Inclusive calendar RangePicker moments → half-open UTC API window.
 * from = start of start day UTC (inclusive)
 * to = start of day after end day UTC (exclusive)
 */
export const rangeToApiDates = (range) => {
  if (!range || !range[0] || !range[1]) return null;
  const startStr = range[0].format('YYYY-MM-DD');
  const endStr = range[1].format('YYYY-MM-DD');
  const from = moment.utc(startStr, 'YYYY-MM-DD').startOf('day').toISOString();
  const to = moment.utc(endStr, 'YYYY-MM-DD').startOf('day').add(1, 'day').toISOString();
  return { from, to };
};

export const validateHalfOpenUtcWindow = (from, to) => {
  if (from == null && to == null) return { ok: true };
  if ((from == null) !== (to == null)) return { ok: false, reason: 'pair' };
  const start = moment.utc(from);
  const end = moment.utc(to);
  if (!start.isValid() || !end.isValid()) return { ok: false, reason: 'invalid' };
  if (!end.isAfter(start)) return { ok: false, reason: 'order' };
  return { ok: true };
};

/** Maps a frozen PascalCase→number enum value to the API EnumMember kebab string. */
export const toEnumMemberValue = (enumObject, raw) => {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'string' && !/^\d+$/.test(raw.trim())) {
    return _.kebabCase(raw.trim());
  }
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  const key = Object.keys(enumObject || {}).find((k) => enumObject[k] === numeric);
  return key ? _.kebabCase(key) : undefined;
};

/** Currency EnumMember is uppercase ISO (USD), not kebab. */
export const toCurrencyApiValue = (raw) => {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'string' && Number.isNaN(Number(raw))) {
    return String(raw).trim().toUpperCase();
  }
  return 'USD';
};

export const normalizePage = ({ skip, top } = {}) => {
  const normalizedSkip = skip == null || skip === '' ? 0 : Number(skip);
  const normalizedTop = top == null || top === '' ? DEFAULT_PAGE_SIZE : Number(top);
  if (
    !Number.isFinite(normalizedSkip) ||
    normalizedSkip < 0 ||
    !Number.isFinite(normalizedTop) ||
    normalizedTop < 1 ||
    normalizedTop > MAX_PAGE_SIZE
  ) {
    return { ok: false };
  }
  return { ok: true, skip: normalizedSkip, top: normalizedTop };
};

export const buildSortToken = (field, direction = 'desc') => {
  if (!field) return undefined;
  const dir = String(direction || 'desc').toLowerCase();
  if (dir !== 'asc' && dir !== 'desc') return undefined;
  return `${field} ${dir}`;
};

/**
 * Builds plain query params for HttpService passthroughParams.
 * Drops null/undefined/''. Does not convert enums unless enumMaps provided.
 *
 * @param {Object} filters
 * @param {Object} [options]
 * @param {Object} [options.enumMaps] map of filterKey → frozen enum object
 * @param {string[]} [options.currencyKeys] keys that use ISO currency wire values
 */
export const buildPassthroughParams = (filters = {}, options = {}) => {
  const { enumMaps = {}, currencyKeys = ['currency'] } = options;
  const params = {};

  Object.keys(filters || {}).forEach((key) => {
    const value = filters[key];
    if (value == null || value === '') return;

    if (currencyKeys.includes(key)) {
      const currency = toCurrencyApiValue(value);
      if (currency) params[key] = currency;
      return;
    }

    if (enumMaps[key]) {
      const wire = toEnumMemberValue(enumMaps[key], value);
      if (wire != null) params[key] = wire;
      return;
    }

    if (typeof value === 'boolean') {
      params[key] = value;
      return;
    }

    params[key] = value;
  });

  return params;
};

/**
 * Maps expandable-filter values (date ranges, enum ids, yes/no, text) to API query fields.
 * Date filters declare apiFrom/apiTo. Fields with excludeFromQuery are omitted.
 */
export const mapModuleFiltersToApi = (filtersConfig = [], moduleFilters = {}) => {
  const api = {};

  filtersConfig.forEach((field) => {
    if (!field || field.excludeFromQuery) return;
    const value = moduleFilters?.[field.name];
    if (value == null || value === '') return;
    if (Array.isArray(value) && value.length === 0) return;

    if (field.type === 'datepicker') {
      const range = rangeToApiDates(value);
      if (!range || !field.apiFrom || !field.apiTo) return;
      api[field.apiFrom] = range.from;
      api[field.apiTo] = range.to;
      return;
    }

    if (field.boolean) {
      if (value === true || value === 'true') api[field.name] = true;
      else if (value === false || value === 'false') api[field.name] = false;
      return;
    }

    api[field.apiName || field.name] = value;
  });

  return api;
};

export const unwrapPaged = (response) => {
  const items = Array.isArray(response)
    ? response
    : Array.isArray(response?.items)
    ? response.items
    : [];
  const totalCount =
    typeof response?.totalCount === 'number'
      ? response.totalCount
      : typeof response?.count === 'number'
      ? response.count
      : items.length;
  return { items, totalCount };
};
